import React, { useState } from 'react';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API, axios } from '../../utils/api';
import { RigaScostamento, usePrezzoZona } from './PrezzoZona';

// Scheda per il cliente di un immobile (admin, scheda cliente → immobile): parere con frase breve per l'elenco,
// testo, "Perché sì" e "Da valutare" (una voce per riga), più i dati che il cliente vede nella scheda.
// Senza testo né voci il cliente non vede la card del parere.

const MAX_BREVE = 140;
const righe = (testo) => testo.split('\n').map((r) => r.trim()).filter(Boolean);

// stile="st": il bottone nel nuovo stile dello staff (scheda cliente), altrimenti quello della pagina vecchia
export const ParereImmobileDialog = ({ immobile, onSalvato, stile }) => {
  const [aperto, setAperto] = useState(false);
  const zona = usePrezzoZona(immobile.id, aperto);
  const [form, setForm] = useState({});
  const [attesa, setAttesa] = useState(false);

  const apri = () => {
    setForm({
      parere_breve: immobile.parere_breve || '',
      parere: immobile.parere || '',
      pro: (immobile.parere_pro || []).join('\n'),
      contro: (immobile.parere_contro || []).join('\n'),
      spese_mensili: immobile.spese_mensili != null ? String(immobile.spese_mensili) : '',
      bagni: immobile.bagni || '',
      piano: immobile.piano || '',
      classe_energetica: immobile.classe_energetica || '',
    });
    setAperto(true);
  };
  const campo = (nome) => ({ value: form[nome] ?? '', onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.value })) });

  const salva = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      await axios.put(`${API}/immobili/${immobile.id}/parere`, {
        parere_breve: form.parere_breve, parere: form.parere, parere_pro: righe(form.pro), parere_contro: righe(form.contro),
        spese_mensili: form.spese_mensili, bagni: form.bagni, piano: form.piano, classe_energetica: form.classe_energetica,
      });
      toast.success('Scheda per il cliente salvata');
      setAperto(false);
      onSalvato();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scheda non salvata');
    } finally {
      setAttesa(false);
    }
  };

  const conParere = immobile.parere || immobile.parere_breve || immobile.parere_pro?.length || immobile.parere_contro?.length;
  return (
    <>
      {stile === 'st' ? (
        <button type="button" className={`btn sm${conParere ? ' p' : ''}`} onClick={apri} data-testid={`parere-${immobile.id}`}>Parere</button>
      ) : (
        <Button variant="outline" size="sm" className={`h-7 text-xs ${conParere ? 'text-gold border-gold/50' : ''}`} onClick={apri}
          title="Parere e dati per il cliente" data-testid={`parere-${immobile.id}`}>
          <Star className="h-3 w-3 mr-1" /> Parere
        </Button>
      )}
      <Dialog open={aperto} onOpenChange={setAperto}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-gold">Scheda per il cliente</DialogTitle>
            <DialogDescription>{immobile.titolo}. Il cliente vede il parere nella scheda e la frase breve nell'elenco.</DialogDescription>
          </DialogHeader>
          <RigaScostamento zona={zona} />
          <form onSubmit={salva} className="space-y-4">
            <div>
              <Label htmlFor="parere_breve">Frase breve (elenco) · {(form.parere_breve || '').length}/{MAX_BREVE}</Label>
              <Input id="parere_breve" maxLength={MAX_BREVE} placeholder="Nel vostro budget, zona che vi piaceva." {...campo('parere_breve')} />
            </div>
            <div>
              <Label htmlFor="parere">Parere</Label>
              <Textarea id="parere" rows={4} placeholder="Piano alto, doppia esposizione, condominio piccolo e ben tenuto…" {...campo('parere')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pro">Perché sì (una per riga)</Label>
                <Textarea id="pro" rows={4} placeholder={'Luce e piano alto\nSpese condominiali basse'} {...campo('pro')} />
              </div>
              <div>
                <Label htmlFor="contro">Da valutare (una per riga)</Label>
                <Textarea id="contro" rows={4} placeholder={'Bagno secondario da rifare\nParcheggio solo in strada'} {...campo('contro')} />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label htmlFor="spese_mensili">Spese €/mese</Label><Input id="spese_mensili" inputMode="decimal" placeholder="120" {...campo('spese_mensili')} /></div>
              <div><Label htmlFor="bagni">Bagni</Label><Input id="bagni" placeholder="2" {...campo('bagni')} /></div>
              <div><Label htmlFor="piano">Piano</Label><Input id="piano" placeholder="4" {...campo('piano')} /></div>
              <div><Label htmlFor="classe_energetica">Classe</Label><Input id="classe_energetica" placeholder="B" {...campo('classe_energetica')} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAperto(false)}>Annulla</Button>
              <Button type="submit" className="btn-gold" disabled={attesa}>{attesa ? 'Salvo…' : 'Salva'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
